import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";

// Adding to a plan, and taking something back out.
//
// TWO WAYS IN, because teachers have material from two places. The catalogue is what somebody
// wrote down — the publisher's modules, the district's adaptation — and taking one in records the
// new row as a variant of it, so "what did this come from" stays answerable. A name typed into the
// box is a shell: a lesson the teacher is teaching that nobody has written down, which is a real
// thing and not a lesser one.
//
// THE FIRST ADD FORKS THE PLAN, and the screen says so. Until then the teacher is following the
// institution's sequence; afterwards they have their own copy of it and their changes are theirs
// alone. That is a thing that happened to their year and they should not have to infer it from a
// heading quietly changing.

interface CatalogueItem {
  resource_id: string;
  name: string;
  within: string | null;
  curriculum: string | null;
  source: string;
  scored: boolean;
}

interface Added {
  forked: boolean;
  copied: number;
  created?: boolean;
  name: string;
  href: string;
}

export function AddToPlan({ kind, classId, unitId, onAdded, onOpen,
                            compact = false }: {
  kind: "unit" | "lesson";
  /** The class whose plan this is: one course, at one school, in one year. */
  classId: string;
  /** The unit a lesson goes into. Absent when adding a unit. */
  unitId?: string;
  onAdded: () => void;
  onOpen: (href: string) => void;
  /** In the nav: a plus on the row, and everything it opens floats over the tree. A full-width
   *  form in a 15rem column is not a form, and a row that grows by ten lines when you press it
   *  moves the plan out from under the person reading it. */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [items, setItems] = useState<CatalogueItem[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<Added | null>(null);
  const box = useRef<HTMLDivElement | null>(null);

  // A POPOVER CLOSES. In the nav this floats over the plan, so the two ways out of anything that
  // covers something else — Escape, and pressing what it is covering — have to work, or the only
  // way back to the tree is to find the plus again and press it a second time.
  useEffect(() => {
    if (!compact || !open) return;
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", key);
    document.addEventListener("mousedown", away);
    return () => { document.removeEventListener("keydown", key);
                   document.removeEventListener("mousedown", away); };
  }, [compact, open]);

  useEffect(() => {
    if (!open || items) return;
    api.get<{ items: CatalogueItem[] }>(`/plan/catalogue?kind=${kind}`)
      .then((r) => setItems(r.items))
      // The catalogue is a convenience; typing a name is the other way in and still works.
      .catch(() => setItems([]));
  }, [open, items, kind]);

  const add = (from?: CatalogueItem) => {
    setBusy(true); setError(null);
    api.post<Added>(`/plan/${kind}s`, {
      class_id: classId,
      ...(unitId ? { unit_id: unitId } : {}),
      ...(from ? { from_resource_id: from.resource_id } : { name }),
    })
      .then((r) => {
        setMade(r); setOpen(false); setName(""); setItems(null);
        onAdded();
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "could not be added"))
      .finally(() => setBusy(false));
  };

  // WHAT WAS ADDED, said once, wherever this is rendered.
  const madeNote = made && (
    <>
      {made.created === false
        ? <>{made.name} is already in your plan. </>
        : <>{made.name} added. </>}
      <button className="btn-link" onClick={() => onOpen(made.href)}>Open it</button>
      {/* The fork, said once, where it happened. */}
      {made.forked && (
        <span className="pe-forked">
          This is your own copy of the plan now — {made.copied} item
          {made.copied === 1 ? "" : "s"} came with it, and your changes are yours alone.
        </span>
      )}
    </>
  );

  const panel = (
    <>
      <div className="pe-row">
        <input value={name} autoFocus disabled={busy}
               placeholder={`What do you call this ${kind}?`}
               onChange={(e) => setName(e.target.value)} />
        <button className="btn primary" disabled={busy || !name.trim()} onClick={() => add()}>
          {busy ? "Adding…" : "Add it"}
        </button>
        <button className="btn" disabled={busy}
                onClick={() => { setOpen(false); setError(null); setName(""); }}>
          Cancel
        </button>
      </div>

      {/* What somebody has already written down. Empty is an ordinary answer: a district that has
          published one module, to a teacher who already has it. */}
      {items && items.length > 0 && (
        <div className="pe-cat">
          <h4>Or take one in</h4>
          <ul>
            {items.map((i) => (
              <li key={i.resource_id}>
                <button disabled={busy} onClick={() => add(i)}>
                  <span className="pe-name">{i.name}</span>
                  <span className="pe-where">
                    {[i.within, i.curriculum].filter(Boolean).join(" · ")}
                  </span>
                  <span className="pe-src">{i.source}</span>
                  {i.scored && <span className="pe-scored">has a rubric</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {items && items.length === 0 && (
        <p className="pv-quiet">
          Nothing to take in: everything published for this course is already in your plan.
        </p>
      )}
      {error && <span className="inc-error">{error}</span>}
    </>
  );

  // IN THE NAV: a plus on the row the new thing goes into. A unit goes into a course and a lesson
  // into a unit, so the control belongs beside the row that names them — and it can be a bare plus
  // precisely because that row is already named. On a page, where nothing names it, it says what
  // it does.
  if (compact) {
    return (
      <div className="pe pe-compact" ref={box}>
        <button className="pe-plus" aria-expanded={open} aria-label={`Add a ${kind}`}
                title={`Add a ${kind}`}
                onClick={() => { setOpen(!open); setMade(null); setError(null); }}>+</button>
        {open && (
          <div className="pe-pop pe-open" role="dialog" aria-label={`Add a ${kind}`}>{panel}</div>
        )}
        {!open && madeNote && (
          <div className="pe-pop pe-made">
            {madeNote}
            <button className="pe-dismiss" aria-label="Dismiss"
                    onClick={() => setMade(null)}>×</button>
          </div>
        )}
        {!open && !made && error && <span className="pe-pop inc-error">{error}</span>}
      </div>
    );
  }

  if (!open) {
    return (
      <div className="pe">
        <button className="btn" onClick={() => { setOpen(true); setMade(null); }}>
          Add a {kind}
        </button>
        {madeNote && <span className="pe-made">{madeNote}</span>}
        {error && <span className="inc-error">{error}</span>}
      </div>
    );
  }

  return <div className="pe pe-open">{panel}</div>;
}


// Taking something out. Two presses, because it is not undoable from here — and the refusal, when
// there is student work under it, is the server's own sentence naming the folder that is in the
// way.
export function RemoveFromPlan({ kind, resourceId, onRemoved }: {
  kind: "unit" | "lesson";
  resourceId: string;
  onRemoved: () => void;
}) {
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = () => {
    setBusy(true); setError(null);
    api.del<{ retired: boolean }>(`/plan/${kind}s/${encodeURIComponent(resourceId)}`)
      .then(() => onRemoved())
      .catch((e: unknown) => {
        setError(e instanceof ApiError ? e.message : "could not be taken out");
        setAsked(false);
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className="pe-remove">
      {!asked && (
        <button className="btn-link" onClick={() => { setAsked(true); setError(null); }}>
          Take this {kind} out of my plan
        </button>
      )}
      {asked && (
        <>
          <span className="pv-quiet">Take it out?</span>
          <button className="btn" disabled={busy} onClick={remove}>
            {busy ? "Taking it out…" : `Yes, remove the ${kind}`}
          </button>
          <button className="btn" disabled={busy} onClick={() => setAsked(false)}>Cancel</button>
        </>
      )}
      {error && <p className="inc-error">{error}</p>}
    </div>
  );
}
